import { 
    UserIcon, 
    Bars3BottomLeftIcon,
    ArrowLeftStartOnRectangleIcon,
    HomeIcon
} from '@heroicons/react/24/outline';
import { 
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Button,
    User,
    Breadcrumbs,
    BreadcrumbItem,
    Spinner,
} from "@nextui-org/react";
import { lupakanPenandaPengumuman } from "../../../services/pengumuman";
import useAuth from "../../../hooks/useAuth";
import RolesAccess from "../../../enums/roles";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from 'js-cookie'
import { LOGIN_PATH, USER_PROFILE } from "../../../services/routes";
import { removeSlug } from '../../../services/helpers';
import { getFetcher } from "../../../services/api";
import useSWR from "swr";

export default function Header({ onOpenSidebar }) {
    const { user, roles } = useAuth()
    const navigate = useNavigate();
    const location = useLocation();


    /*
     * Isi kotak identitas di pojok kanan atas, tiga kemungkinan:
     *
     *   - Super Admin  : cukup "SuperAdmin" saja, tanpa baris peran. Akun ini
     *                    tidak mewakili perusahaan mana pun.
     *   - Akun SIMPEL  : idCompanySimpel di atas, peran di bawahnya - itulah
     *                    identitas yang dikenali petugas saat menelusuri akun.
     *   - Akun lainnya : nama pengguna dan perannya seperti semula, sebab tanpa
     *                    idCompanySimpel angka itu memang tidak ada.
     *
     * Nama lengkap tetap bisa dibaca lewat tooltip pada semua kasus.
     */
    const superAdmin = roles?.includes(RolesAccess.SUPER_ADMIN);
    const idSimpel = user?.companies?.[0]?.company?.idCompanySimpel;
    const identitasAtas = superAdmin ? RolesAccess.SUPER_ADMIN : idSimpel || user?.fullName;
    const peranPengguna = superAdmin ? undefined : roles?.length ? roles.join(", ") : "Pengguna";

    function handleLogout() {
        // Penanda pengumuman ikut dibuang: masuk kembali harus memunculkannya lagi.
        lupakanPenandaPengumuman()
        Cookies.remove('accessToken')
        Cookies.remove('refreshToken')
        Cookies.remove('sessionExpire')
        return navigate(LOGIN_PATH, { replace: true })
    }

    // Fetch running text announcements
    const { data, error, isLoading } = useSWR(
        "/api/announcements/search?isActive=true&type=Running Text",
        getFetcher
    );


    // Topbar App Shell T5 — tinggi 62px, latar surface, garis bawah --line
    return (
        <nav className="w-full fixed top-0 z-30 lg:pl-sidebar bg-surface border-b border-line">
            <div className="flex h-topbar min-w-0 items-center justify-between gap-4 px-[22px]">
                <div className="flex min-w-0 items-center justify-start gap-4">
                    <Button
                        id="sidebar-toggle"
                        onPress={ onOpenSidebar }
                        isIconOnly
                        color="primary"
                        variant="flat"
                        aria-label="Buka menu navigasi"
                        aria-controls="logo-sidebar"
                        className="lg:hidden min-w-[44px] h-[44px]"
                    >
                        <Bars3BottomLeftIcon className="size-5" />
                    </Button>    
                    <Breadcrumbs className="capitalize" color="secondary" isDisabled>
                        <BreadcrumbItem><HomeIcon className="size-4 mb-0.5" /></BreadcrumbItem>
                        {
                            location.pathname.split('/').filter(path => path !== '' && path !== 'admin').map((path) => (
                                <BreadcrumbItem key={path}>{removeSlug(path)}</BreadcrumbItem>
                            ))
                        }
                    </Breadcrumbs>
                </div>
                {/* Running Text */}
                {isLoading ? (
                    <div className="hidden min-w-0 flex-grow items-center justify-center md:flex">
                        <div className="bg-primary-100 px-4 py-2 rounded-md shadow text-primary-800 text-sm w-full max-w-4xl">
                            <Spinner size="sm" color="secondary" />
                        </div>
                    </div>
                ) : error ? (
                    <div className="hidden min-w-0 flex-grow items-center justify-center md:flex">
                        <div className="bg-primary-100 px-4 py-2 rounded-md shadow text-primary-800 text-sm w-300 max-w-4xl">
                            <span>Gagal memuat pengumuman.</span>
                        </div>
                    </div>
                ) : data?.data?.length > 0 ? (
                    <div className="hidden min-w-0 flex-grow items-center justify-center md:flex">
                        <div className="bg-primary-100 px-4 py-2 rounded-md shadow text-primary-800 text-sm w-full max-w-4xl">
                            <marquee behavior="scroll" direction="left" scrollamount="3">
                            <strong>PENGUMUMAN PENTING : </strong>
                                {data?.data?.map((announcement) => announcement.title).join(" • ")}
                            </marquee>
                        </div>
                    </div>
                ) : null}
                <Dropdown placement="bottom-start">
                    <DropdownTrigger>
                        <User 
                            as="button" 
                            name={identitasAtas}
                            description={peranPengguna}
                            title={user.fullName}
                            className="shrink-0 gap-2 rounded-ds-pill border border-line bg-surface-2 py-1 pl-1 pr-1 sm:pr-3"
                            classNames={{ wrapper: "hidden sm:flex" }}
                            avatarProps={{
                                size: "sm",
                                // Inisial tetap dibentuk dari nama orangnya, bukan dari
                                // idCompanySimpel yang berupa angka.
                                src: `https://ui-avatars.com/api/?name=${user.fullName}`
                            }}
                        />
                    </DropdownTrigger>
                    <DropdownMenu variant="faded">
                        <DropdownItem key="settings" startContent={<UserIcon className="size-4" />}>
                            <a href={USER_PROFILE} className="text-sm text-ink-700 hover:text-ink-900 hover:underline">
                                Profile
                            </a>
                        </DropdownItem>
                        <DropdownItem key="logout" startContent={<ArrowLeftStartOnRectangleIcon className="size-4" />}><button onClick={handleLogout}>Logout</button></DropdownItem>
                    </DropdownMenu>
                </Dropdown>  
            </div>
        </nav>
    )
}
